import { sendTracking } from '../../utilities/js/tracking';
import { getOffset, smoothScrollTo } from './../../utilities/js/helper';
import { WidgetInstance } from 'friendly-challenge';
import './form.scss';

class FormHandler {
    constructor ($element, options) {
        const defaults = {
            initAttr: 'data-form',
            controlAttr: 'data-form-control',
            redirectTargetAttr: 'data-form-redirect-target',
            salesforceFormAttr: 'data-form-salesforce',
            validateAttr: 'data-form-validate',
            errorAttr: 'data-form-error',
            buttonDisabledClass: 'button--disabled',
            gibberishValue: 'gibberish',
            captchaValue: 'captcha',
            captchaField: 'frc-captcha-solution',
            scrollOffset: 32
        };

        this.settings = Object.assign({}, defaults, options);
        this.$root = $element;
        this.$form = this.$root.querySelector('form') || this.$root;
        this.$formActionInput = this.$form?.querySelector('#form-action');
        this.$errorLiveRegion = this.$form?.querySelector('[data-form="live-region"]');
        this.$captcha = this.$form?.querySelector('.frc-captcha');
        this.$$requiredFields = this.$form?.querySelectorAll('[required]');
        this.$$validateInputs = this.$form?.querySelectorAll(`[${this.settings.validateAttr}]`);
        this.$firstErrorField = null;
        this.$$gibberishFields = this.$form?.querySelectorAll(`[${this.settings.errorAttr}="${this.settings.gibberishValue}"]`);
        this.$captchaError = this.$form?.querySelector(`[${this.settings.errorAttr}="${this.settings.captchaValue}"]`);
        this.$redirectInput = this.$root.querySelector(`[${this.settings.redirectTargetAttr}]`);
        this.captchaWidget = null;

        // Options
        this.isSalesforceForm = this.$root.hasAttribute(this.settings.salesforceFormAttr);
        this.formAction = this.$formActionInput ? this.$formActionInput.value : null;
        this.requiredFieldsValid = false;
        this.checkGibberish = this.$$gibberishFields.length > 0;
        // The proxy answers 400 with JSON for both gibberish and captcha
        // rejections, so it has to be submitted via fetch whenever either
        // error slot exists - otherwise the browser lands on a raw error page.
        this.usesValidationProxy = this.checkGibberish || !!this.$captchaError;
        this.trackingData = {
            event: 'form_submit',
            elementCategory: 'form',
            elementText: window.location.href
        };

        if (this.$form) {
            this.initialize();
        }
    }

    initialize () {
        this.initForm();

        if (this.$redirectInput && this.$redirectInput.value === '') {
            this.prepareRedirect();
        }
    }

    prepareRedirect () {
        const currentUrl = window.location.origin + window.location.pathname;
        const url = new URL(currentUrl, window.location.origin);
        url.searchParams.set('submitted', this.$redirectInput.getAttribute(this.settings.redirectTargetAttr));
        const finalUrl = url.toString();
        this.$redirectInput.value = finalUrl;
    }

    initForm () {
        this.initFrontendValidation();
        this.initCaptcha();
        this.setEvents();
    }

    initCaptcha () {
        if (this.$captcha) {
            // Kept on the instance so a rejected solution can be reset:
            // FriendlyCaptcha solutions are single use, so after a failed
            // verification the user needs a fresh puzzle.
            this.captchaWidget = new WidgetInstance(this.$captcha);
        }
    }

    showCaptchaError () {
        if (this.$captchaError) {
            const $formField = this.$captchaError.closest('.form-field') || this.$captchaError.parentElement;
            this.toggleErrorState($formField, this.$captchaError, false);
        }

        if (this.$errorLiveRegion) {
            this.$errorLiveRegion.textContent = 'The captcha could not be verified. Please solve it again and resubmit.';
        }

        this.captchaWidget?.reset();
    }

    resetCaptchaError () {
        if (this.$captchaError) {
            const $formField = this.$captchaError.closest('.form-field') || this.$captchaError.parentElement;
            this.toggleErrorState($formField, this.$captchaError, true);
        }
    }

    toggleErrorState ($formField, $error, valid) {
        // Show or hide the specific error element based on validity
        if (!valid) {
            $error.removeAttribute('hidden');
        } else {
            $error.setAttribute('hidden', '');
        }

        // Always set form-field--error if there is at least one visible error child
        const hasVisibleErrors = $formField.querySelectorAll(`[${this.settings.errorAttr}]:not([hidden])`).length > 0;

        if (hasVisibleErrors) {
            $formField.classList.add('form-field--error');
        } else {
            $formField.classList.remove('form-field--error');
        }
    }

    initFrontendValidation () {
        this.$$validateInputs.forEach($validateInput => {
            let valid = false;
            const $formField = $validateInput.closest('.form-field');
            const type = $validateInput.getAttribute(this.settings.validateAttr);
            const $relatedError = $formField?.querySelector(`[${this.settings.errorAttr}="${type}"]`);

            if ($relatedError) {
                $validateInput.addEventListener('focus', () => {
                    this.toggleErrorState($formField, $relatedError, true);
                });

                $validateInput.addEventListener('blur', () => {
                    if ($validateInput.checkValidity() || $validateInput.value == '') {
                        valid = true;
                    } else {
                        valid = false;
                    }

                    this.toggleErrorState($formField, $relatedError, valid);
                });
            }
        });
    }

    checkValidation () {
        this.requiredFieldsValid = true;
        this.$firstErrorField = null;

        this.$$requiredFields.forEach($requiredField => {
            const $formField = $requiredField.closest('.form-field');
            const $relatedError = $formField?.querySelector(`[${this.settings.errorAttr}="required"]`);

            if ($relatedError) {
                if ($requiredField.value === '') {
                    this.requiredFieldsValid = false;

                    if (this.$firstErrorField === null) {
                        this.$firstErrorField = $formField;
                    }
                }

                this.toggleErrorState($formField, $relatedError, $requiredField.value != '');
            }
        });

        if (!this.requiredFieldsValid) {
            this.updateLiveRegion();
            const $firstErrorField = this.$firstErrorField.querySelector('[required]');

            if ($firstErrorField) {
                $firstErrorField.focus();
            }

            const anchorDimensions = getOffset(this.$root);
            const anchorDimensionsTop = anchorDimensions.top - 150;

            smoothScrollTo(0, anchorDimensionsTop, window.OPTIONS.speed);
        }
    }

    updateLiveRegion () {
        if (this.$errorLiveRegion) {
            this.$errorLiveRegion.textContent = 'There are some mandatory fields not yet filled. Please check your data.';
        }
    }

    async submitGibberishForm () {
        const formData = new FormData(this.$form);
        const body = new URLSearchParams(formData);

        try {
            const response = await fetch(this.formAction, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
                },
                body
            });

            if (response.status === 400) {
                let payload;

                try {
                    payload = await response.json();

                    const errors = payload.errors || {};

                    if (this.settings.captchaField in errors) {
                        this.showCaptchaError();
                    } else {
                        this.resetCaptchaError();
                    }

                    this.$$gibberishFields.forEach(($gibberishField) => {
                        const $formField = $gibberishField.closest('.form-field');
                        const $field = $formField?.querySelector('[name]');
                        const fieldName = $field?.name;

                        const hasError = fieldName in errors;

                        this.toggleErrorState($formField, $gibberishField, !hasError);
                    });

                } catch (e) {
                    console.debug('Error parsing the response:', e);
                }
            } else if (response.status === 204) {
                const $ret = this.$form.querySelector('input[name="retURL"]');
                if ($ret && $ret.value) {
                    window.location.href = $ret.value;
                }
            }

        } catch (err) {
            console.error(err);
        }
    }

    submitForm () {
        if (this.isSalesforceForm) {
            this.$form.action = this.formAction;
            this.checkValidation();

            if (this.$form.checkValidity()) {
                if (this.usesValidationProxy) {
                    this.submitGibberishForm();
                } else {
                    this.$form.submit();
                }

                sendTracking(this.trackingData);
            }
        }
    }

    setEvents () {
        this.$form.addEventListener('submit', (e) => {
            e.preventDefault();

            const submitEvent = new CustomEvent('form.submitted', {
                bubbles: true
            });

            this.$form.dispatchEvent(submitEvent);
            this.submitForm();
        });
    }
}

export { FormHandler };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $$form = $context.querySelectorAll('[data-form="root"]');
        for (let i = 0; i < $$form.length; i++) {
            const $form = $$form[i];

            $form.API = new FormHandler($form);
        }
    }
});
